/**
 * Utility for loading 3D models with common patterns.
 * Uses ResourceManager's container cache for both local and remote assets.
 * The model file is only loaded/fetched once; subsequent calls instantiate from cache.
 *
 * Two categories bypass the container cache and load via ImportMeshAsync:
 *
 * 1. Gaussian Splatting formats (.spz, .ply, .splat) — GaussianSplattingMesh
 *    cannot be cloned/instanced through AssetContainer.instantiateModelsToScene
 *    because Mesh.clone() (not GaussianSplattingMesh.clone()) is dispatched,
 *    producing a plain Mesh without the GS rendering pipeline.
 *
 * 2. GLBs using KHR_interactivity — the extension creates a FlowGraphCoordinator
 *    that targets the loaded nodes via glTF object-model pointers. Container-based
 *    loading breaks this because instantiateModelsToScene clones the nodes but the
 *    FlowGraph still references the original container nodes.
 */

import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import type { Mesh } from "@babylonjs/core/Meshes/mesh";
import type { Node } from "@babylonjs/core/node";
import { Scene } from "@babylonjs/core/scene";
import type { InstantiatedEntries } from "@babylonjs/core/assetContainer";
import { ImportMeshAsync } from "@babylonjs/core/Loading/sceneLoader";
import "@babylonjs/loaders/glTF";
import { GetKHRInteractivityImportResults } from "@babylonjs/loaders/glTF/2.0/Extensions/KHR_interactivity.pure";
import "../loaders/gltf/TRACE_materials_occlusion"; // registers the occluder loader extension
import "../loaders/gltf/TRACE_animation_binding"; // stamps loaded nodes with their file's binding names
import { ResourceManager } from "../loaders/ResourceManager";
import { Utility } from "./Utility";
import { SPLAT_EXTENSIONS } from "./AssetTypes";
import { Logger } from "./Logger";
import { instantiateWithGltfPointers } from "./GltfPointers";
import { getAnimationDriver } from "./drivenAnimation";

export interface ModelLoadResult {
    /** The root transform node of the loaded model hierarchy */
    root: TransformNode;
    /** Instantiated entries from container (null for splat and direct-load formats) */
    instantiatedEntries: InstantiatedEntries | null;
    /** Container cache path — caller should call resources.releaseContainer() when done */
    containerPath: string;
    /** glTF extensionsUsed detected during loading */
    extensionsUsed?: string[];
    /** Raw KHR_interactivity extension data from the glTF JSON, if present */
    khrInteractivity?: KHRInteractivityData;
    /**
     * Babylon's KHR_interactivity validation/lowering diagnostics for this
     * load, one `"<json pointer>: <message>"` line each. Babylon validates
     * against the ratified spec and skips (never runs) a graph that fails,
     * while the model itself still loads — so a non-empty list usually means
     * a graph is dead. Present only for direct-load GLBs that reported any.
     */
    khrDiagnostics?: string[];
}

/** Raw KHR_interactivity extension data as it appears in glTF JSON */
export interface KHRInteractivityData {
    graphs: KHRInteractivityGraph[];
    graph?: number;
}

export interface KHRInteractivityGraph {
    types?: KHRInteractivityType[];
    variables?: { type: number; value?: KHRLiteralComponent[]; name?: string }[];
    events?: { id?: string; values?: Record<string, { type: number; value?: KHRLiteralComponent[] }> }[];
    declarations?: KHRInteractivityDeclaration[];
    nodes?: KHRInteractivityNode[];
    /** Subgraph interface: named flow entry points */
    inputFlowSockets?: Record<string, { node: number; socket: string }>;
    /** Subgraph interface: named flow exit points */
    outputFlowSockets?: Record<string, { node: number; socket: string }>;
    /** Subgraph interface: named value inputs with types */
    inputValueSockets?: Record<string, { type: number; description?: string }>;
    /** Subgraph interface: named value outputs */
    outputValueSockets?: Record<string, { node: number; socket: string; type: number; description?: string }>;
}

/**
 * Inline value component. Built-in types are numbers/booleans (`ref` is a
 * JSON-pointer string); Trace custom types (`string`, `color`) are strings.
 */
export type KHRLiteralComponent = boolean | number | string;

/**
 * A `types[]` entry. `signature` is a ratified built-in or `"custom"`; a Trace
 * custom type names itself under `extensions.TRACE_interactivity.type`.
 */
export interface KHRInteractivityType {
    signature: string;
    extensions?: Record<string, { type?: string }>;
}

export interface KHRInteractivityDeclaration {
    op: string;
    /** Extension defining a non-core op (`TRACE_interactivity`, `KHR_node_selectability`, …). */
    extension?: string;
    /** Extension-op input value sockets: id → type index. */
    inputValueSockets?: Record<string, { type: number }>;
    /** Extension-op output value sockets: id → type index. */
    outputValueSockets?: Record<string, { type: number }>;
    extensions?: {
        TRACE_subgraph?: { graph: number; asset?: number };
    };
}

export interface KHRInteractivityNode {
    declaration: number;
    values?: Record<string, { type: number; value?: KHRLiteralComponent[] } | { node: number; socket?: string; type?: number }>;
    flows?: Record<string, { node: number; socket?: string }>;
    configuration?: Record<string, { value?: KHRLiteralComponent[] }>;
    /**
     * glTF application-specific user data. Carries our authoring `metadata`
     * (editor x/y, macro tags) across the KHR boundary — conformant viewers
     * ignore it, well-behaved tooling preserves it.
     */
    extras?: Record<string, unknown>;
}

export interface ModelLoadOptions {
    /** Parent transform to attach the loaded model to */
    parent?: TransformNode;
    /**
     * Whether to reset the loaded model's root transform to identity.
     *
     * - `true` — The GLB root transform is discarded. Used by .trace scenes
     *   via Model, and by Frame behavior which manually positions its mesh
     *   after loading.
     * - `false` (default) — The GLB's baked root transform (e.g. Blender's
     *   Z-up → Y-up rotation) is preserved on the promoted child node.
     */
    resetTransform?: boolean;
    /** File extension override (e.g., ".glb"). If not provided, extracted from src */
    pluginExtension?: string;
    /** Name prefix for instantiated nodes */
    namePrefix?: string;
    /** Clone meshes instead of instancing them. Required when callers need to assign
     *  per-instance materials (InstancedMesh ignores material assignment). Default: false */
    cloneMeshes?: boolean;
}

/** Which loader {@link loadModel} reads a `src` with, and the parser it names. */
interface LoadRoute { via: 'splat' | 'direct' | 'container'; pluginExtension: string }

async function _loadRoute(src: string, resources: ResourceManager, pluginExtensionOverride?: string): Promise<LoadRoute> {
    // The loader follows the FILE, not the `src` string. A library reference
    // (`<api>/assets/{guid}/file`) names no format, but the bytes it resolved
    // to are stored under a name that does (TraceScene import), so an
    // extension-less `src` reads the stored file. `fileExt` then decides both
    // things below: the splat branch, which is how such a `src` reaches the
    // splat loader at all, and the `pluginExtension` fallback. A caller that
    // passes `pluginExtension` has already said what the bytes are, so the
    // stored lookup is skipped and `fileExt` stays the `src`'s own extension.
    const fileExt = Utility.getFileExtensionFromSrc(src)
        || (pluginExtensionOverride ? '' : _storedFileExtension(src, resources));
    const pluginExtension = pluginExtensionOverride
        ?? (fileExt ? `.${fileExt}` : ".glb");

    if (SPLAT_EXTENSIONS.has(fileExt)) return { via: 'splat', pluginExtension };

    // Cheap pre-check: peek at the GLB blob for KHR_interactivity before
    // paying for a full container load. Just a string search on the raw
    // JSON chunk — no JSON.parse, no Babylon involved.
    if (pluginExtension === '.glb' || pluginExtension === '.gltf') {
        const blob = resources.getAsset(src);
        if (blob && await _glbHasInteractivity(blob)) return { via: 'direct', pluginExtension };
    }

    return { via: 'container', pluginExtension };
}

/**
 * Load a 3D model from a URL or local resource.
 */
export async function loadModel(
    src: string,
    scene: Scene,
    resources: ResourceManager,
    options: ModelLoadOptions = {}
): Promise<ModelLoadResult> {
    const { via, pluginExtension } = await _loadRoute(src, resources, options.pluginExtension);
    if (via === 'splat') return _loadSplat(src, scene, resources, pluginExtension, options);
    if (via === 'direct') return _loadDirect(src, scene, resources, pluginExtension, options);
    return _loadViaContainer(src, scene, resources, pluginExtension, options);
}

/**
 * The parser {@link loadModel} reads `src`'s container with when the caller
 * names none, as Model loads, so a caller fetching ahead fills the cache entry
 * that load then hits. Null when the file does not load through the container
 * cache (a splat, or a GLB with KHR_interactivity, which loads directly).
 */
export async function modelContainerExtension(src: string, resources: ResourceManager): Promise<string | null> {
    const { via, pluginExtension } = await _loadRoute(src, resources);
    return via === 'container' ? pluginExtension : null;
}

/**
 * The extension of the stored file an extension-less `src` reaches, `''` when
 * nothing is stored for it (a remote URL keeps the glTF fallback). The import
 * stored the bytes under a key whose name carries the kind the fetch resolved
 * (`TraceScene._registerImportedFile`), so that name is what decides here.
 */
function _storedFileExtension(src: string, resources: ResourceManager): string {
    if (!resources.getAsset(src)) return '';
    return Utility.getFileExtensionFromSrc(resources.resolveUrlToAssetKey(src) ?? src);
}

// Blob bytes are immutable; replacing an asset supplies a new identity.
const _glbInteractivity = new WeakMap<Blob, Promise<boolean>>();

function _glbHasInteractivity(blob: Blob): Promise<boolean> {
    const cached = _glbInteractivity.get(blob);
    if (cached) return cached;
    const pending = _readGlbInteractivity(blob).catch(() => {
        // Keep the existing fallback, but let the next load retry a failed read.
        _glbInteractivity.delete(blob);
        return false;
    });
    _glbInteractivity.set(blob, pending);
    return pending;
}

/**
 * Check if a GLB blob uses KHR_interactivity by scanning the JSON chunk
 * for the extension name string. No JSON.parse — just a raw string search.
 */
async function _readGlbInteractivity(blob: Blob): Promise<boolean> {
    if (blob.size < 20) return false;
    const headerBuf = await blob.slice(0, 20).arrayBuffer();
    const view = new DataView(headerBuf);
    if (view.getUint32(0, true) !== 0x46546C67) return false; // not "glTF"
    const jsonLen = view.getUint32(12, true);
    const jsonBuf = await blob.slice(20, 20 + jsonLen).arrayBuffer();
    const jsonStr = new TextDecoder().decode(jsonBuf);
    return jsonStr.includes('"KHR_interactivity"');
}

// ──────────────────────────────────────────────────────────────────────
// Load paths
// ──────────────────────────────────────────────────────────────────────

async function _loadSplat(
    src: string,
    scene: Scene,
    resources: ResourceManager,
    pluginExtension: string,
    options: ModelLoadOptions
): Promise<ModelLoadResult> {
    // Bundled bytes take precedence over a hosted folder's URL mapping.
    const blob = resources.getAsset(src);
    const url = blob ? URL.createObjectURL(blob) : (resources.resolveAssetUrl(src) ?? src);

    try {
        const result = await ImportMeshAsync(url, scene, { pluginExtension });
        const mesh = result.meshes[0];
        if (!mesh) throw new Error(`No meshes found in ${src}`);

        // SPZ is always RUB (per Niantic spec). Babylon's scaling.y = -1
        // is a wrong-axis correction for RUB→LUF — undo it so standard
        // Scaniverse assets appear right-side-up.
        if (pluginExtension === '.spz') {
            mesh.scaling.y = Math.abs(mesh.scaling.y);
        }
        if (options.parent) {
            mesh.parent = options.parent;
        }

        return { root: mesh, instantiatedEntries: null, containerPath: "" };
    } finally {
        if (blob) URL.revokeObjectURL(url);
    }
}

/**
 * Standard container-based loading. Caches the parsed container so
 * subsequent loads of the same src instantiate from cache.
 */
async function _loadViaContainer(
    src: string,
    scene: Scene,
    resources: ResourceManager,
    pluginExtension: string,
    options: ModelLoadOptions
): Promise<ModelLoadResult> {
    const container = await resources.getOrLoadContainer(src, scene, pluginExtension);

    // nameFunction preserves original node names — without it,
    // instantiateModelsToScene prefixes clones with "Clone of " / "instance of ".
    const nameFunction = options.namePrefix
        ? (name: string) => `${options.namePrefix}_${name}`
        : (name: string) => name;

    // An instance shares its source's MorphTargetManager, so a morph-weight
    // channel animates every placement of the file at once. When the file
    // animates anything, its morph meshes are cloned
    // (the cloned animation groups retarget onto the clone's own targets);
    // a static file keeps instancing, which costs nothing to share.
    const cloneAll = options.cloneMeshes ?? false;
    const animated = container.animationGroups.length > 0;
    const doNotInstantiate = cloneAll || !animated
        ? cloneAll
        : (node: Node) => !!(node as Mesh).morphTargetManager;

    // Clones lose the loader's glTF pointers (and the file names stamped beside
    // them) unless carried across — the address TRACE_animation_binding
    // resolves a bound channel's target by.
    // A DRIVEN animation (`extras.trace.driver`) follows a per-placement
    // driver — this avatar's own face — so what it writes, materials
    // included, must be the placement's own: two orbs must not share a glow.
    const cloneMaterials = container.animationGroups.some(group => getAnimationDriver(group) !== undefined);
    const entries = instantiateWithGltfPointers(
        container,
        nameFunction,
        cloneMaterials,
        { doNotInstantiate }
    );

    if (entries.rootNodes.length === 0) {
        resources.releaseContainer(src);
        throw new Error(`No meshes found in ${src}`);
    }

    const rootNode = entries.rootNodes[0] as TransformNode;
    const root = _processRoot(rootNode, pluginExtension, options);

    return { root, instantiatedEntries: entries, containerPath: src };
}

/**
 * Load a glTF/GLB directly into the scene via ImportMeshAsync, bypassing
 * the container cache. The FlowGraphCoordinator created by KHR_interactivity
 * targets these live scene nodes directly.
 *
 * Uses Babylon's onParsed callback to capture the full extensionsUsed list
 * from the glTF JSON during parsing — no duplicate parse.
 */
async function _loadDirect(
    src: string,
    scene: Scene,
    resources: ResourceManager,
    pluginExtension: string,
    options: ModelLoadOptions,
): Promise<ModelLoadResult> {
    const blob = resources.getAsset(src);
    const url = blob ? URL.createObjectURL(blob) : src;
    // Blob-loaded glTF: external .bin/texture refs resolve via sibling blobs
    // or the asset's registered source base URL.
    const resolver = blob ? resources.createGltfExternalRefResolver(src, url) : undefined;

    let extensionsUsed: string[] | undefined;
    let khrInteractivity: KHRInteractivityData | undefined;
    let parsedJson: object | undefined;
    try {
        const result = await ImportMeshAsync(url, scene, {
            pluginExtension,
            pluginOptions: {
                gltf: {
                    ...(resolver ? { preprocessUrlAsync: resolver.preprocessUrlAsync } : {}),
                    onParsed: (loaderData) => {
                        const json = loaderData.json as Record<string, unknown>;
                        parsedJson = json;
                        extensionsUsed = json?.extensionsUsed as string[] | undefined;
                        const extensions = json?.extensions as Record<string, unknown> | undefined;
                        khrInteractivity = extensions?.KHR_interactivity as KHRInteractivityData | undefined;
                    }
                }
            }
        });
        const rootNode = result.transformNodes.find(n => n.name === '__root__')
            ?? result.meshes[0];
        if (!rootNode) throw new Error(`No meshes found in ${src}`);

        const root = _processRoot(rootNode, pluginExtension, options);
        const khrDiagnostics = _collectKHRDiagnostics(scene, parsedJson, src);

        return {
            root, instantiatedEntries: null, containerPath: "", extensionsUsed, khrInteractivity,
            ...(khrDiagnostics.length > 0 ? { khrDiagnostics } : {}),
        };
    } finally {
        if (blob) URL.revokeObjectURL(url);
        resolver?.dispose();
    }
}

/**
 * Babylon's KHR_interactivity diagnostics for one load. The extension's
 * `onReady` runs (and is awaited) before ImportMeshAsync resolves, so the
 * import result is complete here. Results accumulate per scene across every
 * asset loaded into it, and `GetKHRInteractivityImportResult` only returns
 * the newest — which a concurrent load may already have replaced. The result
 * keeps the loader's glTF JSON, the same object `onParsed` saw, so match on
 * that identity instead.
 */
function _collectKHRDiagnostics(scene: Scene, gltfJson: object | undefined, src: string): string[] {
    if (!gltfJson) return [];
    const result = GetKHRInteractivityImportResults(scene).find(r => r.glTF === gltfJson);
    if (!result) return [];

    const messages = result.document.diagnostics.map(d => `${d.path}: ${d.message}`);
    const flagged: number[] = [];
    for (const graphResult of result.graphs) {
        if (graphResult.diagnostics.length === 0) continue;
        flagged.push(graphResult.graph.index);
        for (const d of graphResult.diagnostics) messages.push(`${d.path}: ${d.message}`);
    }
    if (messages.length > 0) {
        const graphs = flagged.length > 0 ? ` (graph ${flagged.map(i => `#${i}`).join(', ')})` : '';
        Logger.warn(`[ModelLoader] KHR_interactivity in ${src}: ${messages.length} diagnostic(s)${graphs} — non-conformant graphs are skipped. First: ${messages[0]}`);
    }
    return messages;
}

// ──────────────────────────────────────────────────────────────────────

function _processRoot(
    rootNode: TransformNode,
    pluginExtension: string,
    options: ModelLoadOptions
): TransformNode {
    const children = rootNode.getChildren<TransformNode>();
    const isGltf = pluginExtension === '.glb' || pluginExtension === '.gltf';
    const reset = options.resetTransform ?? false;

    if (children.length === 1 && isGltf) {
        // Babylon's __root__ is always identity — promote the single child
        // directly and dispose __root__ to avoid an unnecessary wrapper node.
        const child = children[0];

        if (reset) {
            // Discard the child's baked GLB root transform (e.g. Blender's
            // Z-up → Y-up rotation). Only the immediate child is reset;
            // deeper nodes are model content and must be preserved.
            child.position.setAll(0);
            child.rotationQuaternion = null;
            child.rotation.setAll(0);
            child.scaling.setAll(1);
        }

        // Unparent child BEFORE disposing rootNode, otherwise dispose
        // cascades to the child.
        child.parent = options.parent ?? null;
        rootNode.dispose();
        return child;
    }

    if (options.parent) {
        rootNode.parent = options.parent;
    }
    if (reset) {
        rootNode.position.setAll(0);
        rootNode.rotationQuaternion = null;
        rootNode.rotation.setAll(0);
        rootNode.scaling.setAll(1);
    }
    return rootNode;
}
